// @flow
import store from '../stores/configureStore'
import { trackLinkWithData } from '../../lib/analytics/fpti'
import { get } from 'lodash'
import log from 'loglevel'
import qs from 'qs'

type PispRedirectData = {
  returnUrl: string,
  status: string,
  returnCode: string,
  transactionId?: string,
  milliseconds?: number,
  transactionType?: string,
}

/*
 *  Redirect user incase of error
 *  @param errorCategory - string used to pass back with partner url
 *
 *  Partner will need to provide cancel_url or return_url
 */
export function handleErrorRedirect(
  errorCategory: string = 'INTERNAL_SERVER_ERROR'
) {
  const { return_url, cancel_url } = getPsd2FlowData()
  const redirectData = {
    returnUrl: cancel_url || return_url,
    status: 'failed',
    returnCode: errorCategory,
    milliseconds: 0,
  }

  trackPispReturnUrl(return_url, errorCategory)
  return handleRedirect(redirectData)
}

/*
 *  Typically used for handling the case where user hits back
 *  User will be redirected back with returnCode cancelled
 *
 *  Partner will need to provide cancel_url or return_url
 */
export function handleCancelRedirect() {
  const { cancel_url, return_url } = getPsd2FlowData()
  const returnCode = 'USER_CANCELLED'
  const redirectData = {
    returnUrl: cancel_url || return_url,
    status: 'failed',
    milliseconds: 0,
    returnCode,
  }

  trackPispReturnUrl(return_url, returnCode)
  return handleRedirect(redirectData)
}

/*
 *  Used for handling success redirect case
 *  Partner will need to provide return_url
 */
export function handleSuccessRedirect(transactionId: string = '') {
  const { return_url } = getPsd2FlowData()
  const returnCode = 'SUCCESS'
  const redirectData = {
    returnUrl: return_url,
    status: 'success',
    transactionId: transactionId,
    returnCode,
    milliseconds: 0,
  }

  trackPispReturnUrl(return_url, returnCode)
  return handleRedirect(redirectData)
}

/*
 *  Used for handling success redirect case in PPCP flow
 *  transactionType needs to be bassed as query param
 *  for appropriate tracking on ppcp
 */
export function handlePpcpSuccessRedirect(
  transactionId: string = '',
  transactionType: string = ''
) {
  const { return_url } = getPsd2FlowData()

  const redirectData = {
    returnUrl: return_url,
    status: 'success',
    transactionId: transactionId,
    returnCode: 'SUCCESS',
    transactionType,
  }

  return handleRedirect(redirectData)
}

/*
 *  Used for handling the redirect piece for the above redirection cases
 *  @param PispRedirectData - Object containing neccesary data needed to pass
 *         back to partner with redirection URL
 */
function handleRedirect({
  returnUrl = '',
  status = 'failed',
  transactionId = '',
  returnCode = '',
  milliseconds = 2000,
  transactionType = '',
}: PispRedirectData) {
  // Unlikely scenerio, but if no returnUrl, user will return to myaccount/transfer
  let redirectUrl = '/myaccount/transfer'
  if (returnUrl) {
    // Mandatory query params
    const queryParams = {
      status,
      return_code: returnCode,
    }

    // Get query string, add options to disable encoding
    const queryString = qs.stringify(queryParams, { encode: false })

    // Only append transactionId param if available
    const transactionIdParam = transactionId
      ? `&transactionId=${transactionId}`
      : ''

    // used for PPCP flow
    const transactionTypeParam = transactionType.length
      ? `&transactionType=${transactionType}`
      : ''

    // Check if returnUrl already has query params, use '&' otherwise use '?'
    const delimiter = returnUrl.includes('?') ? '&' : '?'

    redirectUrl = `${returnUrl}${delimiter}${queryString}${transactionIdParam}${transactionTypeParam}`
  } else {
    log.warn('pisp client error: no returnUrl was provided')
  }

  redirectTimeout(redirectUrl, milliseconds)

  // return data here to validate tests
  return { returnUrl, status, transactionId, returnCode }
}

// Redirect user to redirectUrl after a number of milliseconds
// Currently defaulted to two seconds
function redirectTimeout(redirectUrl: string, milliseconds: number) {
  setTimeout(() => window.open(redirectUrl, '_self'), milliseconds)
}

// This function tracks the return_url within the link FPTI data attribute,
// this allows us to more easily resolve any issues relating to redirecting
// back to client.
export function trackPispReturnUrl(
  returnUrl: string = '',
  returnCode: string = ''
) {
  const linkName = 'redirect_to_partner'
  const analyticsData = {
    ret_url: returnUrl,
    partner_ret_enum: returnCode,
  }
  trackLinkWithData(linkName, analyticsData, 'link')
}

/*
 *  Grab pisp flow data from redux store
 *  @returns Object - containing return_url and cancel_url that is
 *           used to redirect user back
 */
function getPsd2FlowData() {
  const getState = get(store, 'getState', '')
  const reduxState = (typeof getState === 'function' && getState()) || {}
  const {
    server: { psd2: { return_url = '', cancel_url = '' } = {} } = {},
  } = reduxState
  return { return_url, cancel_url }
}
